import React, { useRef } from 'react'
import {
  UseFormRegister,
  UseFormHandleSubmit,
  useFieldArray,
  Control,
  useWatch,
  UseFormSetValue,
} from 'react-hook-form'
import {
  SlidePartners,
  PartnerItem,
  PartnersFormData,
} from '../hooks/partners/types'
import { SlideContainer } from '../components/slide_container'
import { SlideHeader } from '../components/slide_header'
import {
  AiRegenerateSection,
  AiInstructionFormData,
} from '../components/ai_regenerate_section'
import FlatButton from '../../../components/buttons/flat_button'
import { TextInput } from '../../../components/form/inputs/TextInput'
import { TextareaInput } from '../../../components/form/inputs/TextareaInput'
import { ListGroup } from '../components/list/list_group'
import { EvaluationStarIcon } from '../../../components/common/star_icons'
import { StarRating } from '../../../components/common/star_rating'
import { useImageUpload } from '../../../hooks/use_image_upload'
import NoImage from '../../../images/noimage.png'
import logoGray from '../../../images/logo_gray.png'

interface PartnersSlideProps {
  slidePartners: SlidePartners | null
  isEditing: boolean
  isUpdating: boolean
  isDirty: boolean
  register: UseFormRegister<PartnersFormData>
  handleSubmit: UseFormHandleSubmit<PartnersFormData>
  control: Control<PartnersFormData>
  setValue: UseFormSetValue<PartnersFormData>
  registerAi: UseFormRegister<AiInstructionFormData>
  handleSubmitAi: UseFormHandleSubmit<AiInstructionFormData>
  aiInstruction: string
  onFormSubmit: (data: PartnersFormData) => Promise<void>
  onAiRegenerate: (data: AiInstructionFormData) => Promise<void>
}

export const PartnersSlide = React.forwardRef<
  HTMLDivElement,
  PartnersSlideProps
>(
  (
    {
      slidePartners,
      isEditing,
      isUpdating,
      isDirty,
      register,
      handleSubmit,
      control,
      setValue,
      registerAi,
      handleSubmitAi,
      aiInstruction,
      onFormSubmit,
      onAiRegenerate,
    },
    ref
  ) => {
    const partnerFileInputRefs = useRef<(HTMLInputElement | null)[]>([])
    const { upload } = useImageUpload()
    const {
      fields: partnerFields,
      append: appendPartner,
      remove: removePartner,
      update: updatePartner,
    } = useFieldArray({
      control,
      name: 'partners',
    })

    // 現在のフォームの値を監視
    const watchedPartners = useWatch({
      control,
      name: 'partners',
    })

    // パートナー企業画像のアップロード処理
    const handlePartnerImageUpload = async (
      event: React.ChangeEvent<HTMLInputElement>,
      index: number
    ) => {
      const file = event.target.files?.[0]
      if (!file) return

      const response = await upload(file)

      if (response !== null) {
        const currentPartners = watchedPartners || []
        const updatedPartner = {
          ...currentPartners[index],
          thumbnailUrl: response.url,
        }
        setValue(`partners.${index}.thumbnailUrl`, response.url)
        updatePartner(index, updatedPartner)
      }
    }

    const handleAddPartner = () => {
      appendPartner({
        thumbnailUrl: null,
        enterpriseName: '',
        industryCategory: '',
        evaluation: 5,
        enterpriseFeature: '',
        advantage: '',
      })
    }

    if (!slidePartners) {
      return (
        <div className="flex items-center justify-center h-64">
          <p>オススメ共創パートナー企業が見つかりません</p>
        </div>
      )
    }

    return (
      <div ref={ref}>
        <SlideContainer>
          <SlideHeader
            title="6. オススメ共創パートナー企業"
            isEditing={isEditing}
            isUpdating={isUpdating}
            isDirty={isDirty}
            onUpdate={handleSubmit(onFormSubmit)}
          />

          <div className="slide-content px-8 py-6">
            {isEditing ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {partnerFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="bg-white border border-main-gray rounded-lg p-6"
                    >
                      <div className="flex items-start mb-4">
                        <div className="flex flex-col items-center mr-2">
                          <img
                            src={field.thumbnailUrl || NoImage}
                            alt="パートナー企業"
                            className="w-16 h-16 rounded-full object-cover mb-2"
                          />
                          <label
                            htmlFor={`partner-image-upload-${index}`}
                            className="text-main-green p-2 border border-main-green cursor-pointer inline-block text-xs hover:opacity-50 transition-opacity duration-300 rounded-md w-20 text-center"
                          >
                            画像を変更
                          </label>
                          <input
                            id={`partner-image-upload-${index}`}
                            ref={(el) => {
                              partnerFileInputRefs.current[index] = el
                            }}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handlePartnerImageUpload(e, index)}
                          />
                        </div>
                        <div className="flex-1">
                          <TextInput
                            register={register(
                              `partners.${index}.enterpriseName`
                            )}
                            placeholder="企業名"
                            className="mb-2"
                          />
                          <TextInput
                            register={register(
                              `partners.${index}.industryCategory`
                            )}
                            placeholder="業界カテゴリ"
                          />
                        </div>
                      </div>
                      <div className="flex mb-2">
                        <div className="flex items-center flex-1">
                          <p className="font-bold mr-1">・オススメ度：</p>
                          <div className="border border-main-gray rounded-md p-2">
                            <StarRating
                              rating={field.evaluation}
                              onChange={(rating) => {
                                const currentValues =
                                  watchedPartners?.[index] || field

                                updatePartner(index, {
                                  ...currentValues,
                                  evaluation: rating,
                                })
                              }}
                              size="md"
                            />
                            <input
                              type="hidden"
                              {...register(`partners.${index}.evaluation`, {
                                valueAsNumber: true,
                              })}
                              value={field.evaluation}
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="flex">
                          <p className="font-bold text-base mb-1">・</p>
                          <div className="flex-1">
                            <p className="font-bold text-base mb-1">
                              企業・団体の特徴：
                            </p>
                            <div className="text-sm mb-3">
                              <TextareaInput
                                register={register(
                                  `partners.${index}.enterpriseFeature`
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="flex">
                          <p className="font-bold text-base mb-1">・</p>
                          <div className="flex-1">
                            <p className="font-bold text-base mb-1">
                              メリット：
                            </p>
                            <div className="text-sm mb-3">
                              <TextareaInput
                                register={register(
                                  `partners.${index}.advantage`
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <FlatButton
                          type="button"
                          onClick={() => removePartner(index)}
                          disabled={isUpdating || partnerFields.length <= 1}
                          className="!bg-main-red w-20"
                        >
                          削除
                        </FlatButton>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <FlatButton onClick={handleAddPartner}>
                    パートナー企業を追加
                  </FlatButton>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(() => {
                  try {
                    const partners = JSON.parse(
                      slidePartners.partners || '[]'
                    ) as PartnerItem[]
                    return partners.map((partner, index) => (
                      <div
                        key={index}
                        className="bg-white border border-main-gray rounded-lg p-6"
                      >
                        <div className="flex items-start mb-4">
                          <img
                            src={partner.thumbnailUrl || NoImage}
                            alt={partner.enterpriseName}
                            className="w-12 h-12 rounded-full object-cover mr-4"
                          />
                          <div className="flex-1">
                            <p className="font-bold text-lg mb-1">
                              {partner.enterpriseName}
                            </p>
                            <p className="text-sm">
                              {partner.industryCategory}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start mb-2">
                          <span className="font-bold mr-1">・オススメ度：</span>
                          <div className="mt-1">
                            <EvaluationStarIcon
                              evaluation={partner.evaluation}
                            />
                          </div>
                        </div>
                        <ListGroup
                          title=""
                          isEditing={false}
                          items={[
                            {
                              label: '企業・団体の特徴',
                              value: partner.enterpriseFeature,
                              fieldName: 'enterpriseFeature',
                              rows: 4,
                            },
                            {
                              label: 'メリット',
                              value: partner.advantage,
                              fieldName: 'advantage',
                              rows: 4,
                            },
                          ]}
                        />
                      </div>
                    ))
                  } catch (error) {
                    console.error('パートナー企業データのパースエラー:', error)
                    return (
                      <div className="col-span-full">
                        <p className="text-main-red text-center">
                          パートナー企業データの読み込みに失敗しました。
                        </p>
                      </div>
                    )
                  }
                })()}
              </div>
            )}
            {isEditing && (
              <div className="mt-8">
                <AiRegenerateSection
                  register={registerAi}
                  handleSubmit={handleSubmitAi}
                  aiInstruction={aiInstruction}
                  isUpdating={isUpdating}
                  onSubmit={onAiRegenerate}
                />
              </div>
            )}
            <div className="slide-footer mt-20">
              <p className="text-xs text-main-darkgray text-center mb-2">
                この企画書は「SDGs 共創
                Lodge」の共創アイディア生成AIで生成しました。
              </p>
              <img
                src={logoGray}
                alt="SDGs共創Lodge Logo"
                className="absolute bottom-2 right-4 w-20 h-auto"
              />
            </div>
          </div>
        </SlideContainer>
      </div>
    )
  }
)

PartnersSlide.displayName = 'PartnersSlide'
